import { useEffect, useMemo, useState } from "react";

import { api, type ApiError } from "./client";
import { useResource } from "./useResource";
import { useRealtimeScope } from "../realtime/useRealtimeScope";
import type { TaskStatus } from "../types";

// The Runners surface is the sandbox-host fleet (specifications/runners.md).
// A "runner" is one `sandbox_hosts` row; the fleet is global (single-tenant).

export type RunnerStatus = "draining" | "offline" | "online" | "unconnected";

// A runner Tinsmith launched for the workspace (cloud-runners.md). Tinsmith
// manages it: every fleet mutation refuses it.
export type RunnerCloud = {
  // A catalog size key, e.g. "small".
  readonly size: string;
  readonly status: string;
};

// DTOs (snake_case) — mirror the GET /api/v1/runners JSON. These live here
// rather than the shared types.ts since they're specific to this surface.
export type ApiRunner = {
  readonly agents_running: number | string;
  readonly auth_token_prefix: string | null;
  readonly booted_at: string | null;
  readonly capacity_total: number | string;
  // Null for a runner someone enrolled.
  readonly cloud: RunnerCloud | null;
  readonly cpu_pct: number | string | null;
  readonly disk_bytes: number | string;
  readonly disk_pct: number | string | null;
  readonly enrolled_at: string | null;
  readonly hostname: string;
  readonly last_heartbeat_at: string | null;
  readonly mem_pct: number | string | null;
  readonly memory_bytes: number | string;
  readonly public_id: string;
  readonly restart_requested_at: string | null;
  readonly status: RunnerStatus;
  readonly vcpus: number | string;
  readonly version: string | null;
};

export type ApiRunnerSummary = {
  readonly agents_running: number | string;
  readonly online: number | string;
  readonly total: number | string;
  readonly vcpus_available: number | string;
};

export type ApiRunnersResponse = {
  readonly runners: ReadonlyArray<ApiRunner>;
  readonly summary: ApiRunnerSummary;
  // The viewer's workspace, for the `runner.updated` realtime subscription
  // (the fleet is global but the event rides the workspace scope).
  readonly workspace_public_id: string;
};

// Fleet-list / detail-panel view model.
export type Runner = {
  readonly agents: number;
  // Concurrent sandbox slots the host advertised at handshake (`--capacity`).
  // 0 for a host that has never dialled in, so it has never said.
  readonly capacity: number;
  readonly cloud: RunnerCloud | null;
  readonly cpu: number | null;
  readonly disk: number | null;
  readonly id: string;
  readonly mem: number | null;
  readonly memoryGb: number;
  readonly name: string;
  // An operator Restart is in flight: the host took the command and is
  // bouncing. Cleared by the re-handshake, and by the server once no plausible
  // bounce could still be running.
  readonly restartPending: boolean;
  readonly status: RunnerStatus;
  readonly uptime: string;
  readonly vcpu: number;
  readonly version: string | null;
};

export type RunnerSummary = {
  readonly agentsRunning: number;
  readonly online: number;
  readonly total: number;
  readonly vcpusAvailable: number;
};

export type RunnerStatusMeta = {
  readonly dotColor?: string;
  readonly label: string;
  // Maps onto the Pill component's status palette; omitted = neutral pill.
  readonly pill?: TaskStatus;
};

// Pills per runners.md "Status model": online green, draining amber, offline
// neutral/off, unconnected neutral/blue.
export const RUNNER_STATUS_META: Record<RunnerStatus, RunnerStatusMeta> = {
  draining: { label: "Draining", pill: "needs-input" },
  offline: { label: "Offline" },
  online: { label: "Online", pill: "done" },
  unconnected: { dotColor: "var(--accent)", label: "Unconnected" },
};

// Postgres numerics/bigints arrive as strings; coerce defensively.
const num = (value: number | string | null): number => {
  if (value == null) {
    return 0;
  }
  const n = typeof value === "number" ? value : Number.parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

const GIB = 1024 ** 3;

// A meter reads `—` (null) whenever the host isn't reporting telemetry — i.e.
// offline (lost) or unconnected (never connected). See runners.md.
export const isReporting = (status: RunnerStatus): boolean =>
  status !== "offline" && status !== "unconnected";

// Agents against advertised slots — `3/4` — for the fleet list's Agents cell
// and the panel's Capacity block. A host that isn't reporting has nothing to
// count (`—`); one that has never advertised a capacity shows the bare count
// rather than a denominator it never gave.
export const runnerSlots = (runner: Runner): string => {
  if (!isReporting(runner.status)) {
    return "—";
  }
  return runner.capacity > 0
    ? `${runner.agents}/${runner.capacity}`
    : `${runner.agents}`;
};

const humanizeUptime = (
  bootedAt: string | null,
  status: RunnerStatus,
): string => {
  if (!bootedAt || !isReporting(status)) {
    return "—";
  }
  const ms = Date.now() - Date.parse(bootedAt);
  if (!Number.isFinite(ms) || ms < 0) {
    return "—";
  }
  const mins = Math.floor(ms / 60000);
  if (mins < 60) {
    return `${mins}m`;
  }
  const hours = Math.floor(mins / 60);
  if (hours < 24) {
    return `${hours}h ${mins % 60}m`;
  }
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
};

export const mapRunner = (dto: ApiRunner): Runner => {
  const reports = isReporting(dto.status);
  return {
    agents: num(dto.agents_running),
    capacity: num(dto.capacity_total),
    cloud: dto.cloud,
    cpu: reports ? num(dto.cpu_pct) : null,
    disk: reports ? num(dto.disk_pct) : null,
    id: dto.public_id,
    mem: reports ? num(dto.mem_pct) : null,
    memoryGb: Math.round(num(dto.memory_bytes) / GIB),
    name: dto.hostname,
    restartPending: dto.restart_requested_at != null,
    status: dto.status,
    uptime: humanizeUptime(dto.booted_at, dto.status),
    vcpu: num(dto.vcpus),
    version: dto.version,
  };
};

const mapSummary = (dto: ApiRunnerSummary): RunnerSummary => ({
  agentsRunning: num(dto.agents_running),
  online: num(dto.online),
  total: num(dto.total),
  vcpusAvailable: num(dto.vcpus_available),
});

const EMPTY_SUMMARY: RunnerSummary = {
  agentsRunning: 0,
  online: 0,
  total: 0,
  vcpusAvailable: 0,
};

export type UseRunners = {
  readonly error: ApiError | undefined;
  readonly loading: boolean;
  readonly refetch: () => void;
  readonly runners: ReadonlyArray<Runner>;
  readonly summary: RunnerSummary;
};

// The mutable telemetry a `runner.updated` event carries for one host
// (realtime.md §Sandbox-host telemetry). Applied over the last GET snapshot.
type RunnerPatch = {
  readonly agents?: number;
  readonly cpu_pct?: number | null;
  readonly disk_pct?: number | null;
  readonly mem_pct?: number | null;
  readonly status?: RunnerStatus;
};

// Overlay one live patch onto a host row. A meter field is only overwritten
// when the event carries it (so a pure status change keeps the last sample),
// and an explicit `null` — the host stopped reporting — is honoured.
const applyPatch = (r: ApiRunner, p: RunnerPatch): ApiRunner => ({
  ...r,
  agents_running: p.agents ?? r.agents_running,
  cpu_pct: p.cpu_pct === undefined ? r.cpu_pct : p.cpu_pct,
  disk_pct: p.disk_pct === undefined ? r.disk_pct : p.disk_pct,
  mem_pct: p.mem_pct === undefined ? r.mem_pct : p.mem_pct,
  status: p.status ?? r.status,
});

// Re-derive the summary cards from live-patched rows, mirroring the server
// rollup: vCPU-available and the online count consider `online` hosts only.
const recomputeSummary = (rows: ReadonlyArray<ApiRunner>): RunnerSummary => {
  let agentsRunning = 0;
  let online = 0;
  let vcpusAvailable = 0;
  for (const r of rows) {
    agentsRunning += num(r.agents_running);
    if (r.status === "online") {
      online += 1;
      vcpusAvailable += num(r.vcpus);
    }
  }
  return { agentsRunning, online, total: rows.length, vcpusAvailable };
};

export const useRunners = (): UseRunners => {
  const { data, error, loading, refetch } =
    useResource<ApiRunnersResponse>("/runners");
  const [patches, setPatches] = useState<ReadonlyMap<string, RunnerPatch>>(
    () => new Map(),
  );

  // A fresh GET snapshot supersedes any accumulated live patches.
  useEffect(() => {
    setPatches(new Map());
  }, [data]);

  // The fleet is global, but `runner.updated` rides the viewer's `workspace:`
  // scope (realtime.md B3). Patch the affected row's telemetry/status in place
  // so meters, pills, and the summary cards update without a refetch.
  useRealtimeScope(
    data?.workspace_public_id ? `workspace:${data.workspace_public_id}` : null,
    (event) => {
      if (event.kind !== "runner.updated") {
        return;
      }
      const ref = event.ref as { public_id?: string } | undefined;
      const id = ref?.public_id;
      if (!id) {
        return;
      }
      // A host this page has never seen — someone else just enrolled one — has
      // no row to patch, so take the snapshot again. An `unconnected` host
      // reports no telemetry, so nothing else would ever bring it in.
      if (!data?.runners.some((r) => r.public_id === id)) {
        refetch();
        return;
      }
      const patch = (event.summary ?? {}) as RunnerPatch;
      setPatches((prev) => {
        const next = new Map(prev);
        next.set(id, { ...prev.get(id), ...patch });
        return next;
      });
    },
  );

  const patched = useMemo<ReadonlyArray<ApiRunner>>(() => {
    const base = data?.runners ?? [];
    if (patches.size === 0) {
      return base;
    }
    return base.map((r) => {
      const patch = patches.get(r.public_id);
      return patch ? applyPatch(r, patch) : r;
    });
  }, [data, patches]);

  const runners = useMemo(() => patched.map(mapRunner), [patched]);
  const summary = useMemo(() => {
    if (!data) {
      return EMPTY_SUMMARY;
    }
    // Unpatched: trust the server's authoritative rollup verbatim.
    return patches.size === 0
      ? mapSummary(data.summary)
      : recomputeSummary(patched);
  }, [data, patched, patches]);
  return { error, loading, refetch, runners, summary };
};

// Enrollment (runners.md "Enrollment"). Returns the full one-time
// `tcn_rnr_…` token exactly once — it is never retrievable again.
export type EnrollRunnerInput = {
  readonly name: string;
};

export type EnrolledRunner = {
  readonly runner: ApiRunner;
  readonly token: string;
};

export const enrollRunner = (
  input: EnrollRunnerInput,
): Promise<EnrolledRunner> =>
  api.post<EnrolledRunner>("/runners", { name: input.name });

// Lifecycle actions (admin/owner). Optimistic on the client; the caller
// refetches, or the fleet live-patches from the `runner.updated` event, to
// confirm.
export const drainRunner = (id: string): Promise<unknown> =>
  api.post(`/runners/${id}/drain`);

export const resumeRunner = (id: string): Promise<unknown> =>
  api.post(`/runners/${id}/resume`);

export const restartRunner = (id: string): Promise<unknown> =>
  api.post(`/runners/${id}/restart`);

export const removeRunner = (id: string): Promise<unknown> =>
  api.del(`/runners/${id}`);

// Rotate the host bearer (runners.md "Rotate & revoke"). Returns the new
// `tcn_rnr_…` token exactly once — the old one stops working immediately.
export const rotateRunnerKey = (id: string): Promise<EnrolledRunner> =>
  api.post<EnrolledRunner>(`/runners/${id}/rotate-key`);
